'use client'

import { useClientLabels } from 'labels/hooks/useClientLabels/useClientLabels'
import type { StringTranslationFunction } from 'labels/types'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { createContext, type ReactNode, useContext, useMemo } from 'react'
import { LabelsNamespaces } from 'types/labels'

export const StateMenuLabelsContext = createContext<
	| {
			isLoading: boolean | undefined
			t: StringTranslationFunction
	  }
	| undefined
>(undefined)

interface MenuLabelsProviderProps {
	readonly firstOpen: boolean
	readonly children: ReactNode
}

export function MenuLabelsProvider({
	firstOpen,
	children,
}: MenuLabelsProviderProps) {
	const {
		isShop,
		country: { countryISO, languageISO, locale },
	} = useMasterData()

	const menuNamespace = isShop
		? LabelsNamespaces.Menu_Shop
		: LabelsNamespaces.Menu_Outlet

	const { isLoading, t } = useClientLabels({
		namespaces: firstOpen ? [menuNamespace, LabelsNamespaces.Menu_Common] : [],
		suspense: false,
		countryISO,
		languageISO,
		locale,
	})

	const value = useMemo(
		() => ({
			isLoading,
			t,
		}),
		[isLoading, t]
	)

	return (
		<StateMenuLabelsContext.Provider value={value}>
			{children}
		</StateMenuLabelsContext.Provider>
	)
}

export const useMenuLabelsContext = () => {
	const context = useContext(StateMenuLabelsContext)
	if (!context) {
		throw new Error(
			'useMenuLabelsContext must be used within a MenuLabelsProvider'
		)
	}
	return context
}
